// frontend/src/api/exchange-sync.ts
// Typed API client for Exchange API Sync feature (Chunk 186)
import apiClient from './client';

export type SyncExchange = 'coinbase' | 'binance' | 'kraken';

export type SyncStatus = 'active' | 'error' | 'revoked';
export type ExchangeHealthState = 'healthy' | 'stale' | 'blocked' | 'never_synced';
export type ExchangeHealthReason =
  | 'recent_successful_sync'
  | 'stale_sync'
  | 'sync_error'
  | 'never_synced'
  | 'provider_credentials_required'
  | 'provider_auth_required'
  | 'provider_replay_blocked';
export type ExchangeFreshnessBucket = 'fresh' | 'stale' | 'never_synced';
export type ExchangeBlockerClassification = 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
export type ExchangeRunnerWaitState = 'runner_limited_wait' | null;
export type ExchangeFreshnessState = 'healthy' | 'stale_verified' | 'never_synced';
export type ExchangePrimaryNextStep = 'sync_now' | 'retry_sync' | 'manage_connection' | null;
export type ExchangeDominantAction =
  | 'review_last_successful_sync'
  | 'sync_now'
  | 'retry_sync'
  | 'manage_connection'
  | null;

export interface LastSuccessfulSyncProof {
  available: boolean;
  synced_at: string | null;
  freshness_bucket: ExchangeFreshnessBucket;
  freshness_stale_hours: number | null;
  proof_source: 'exchange_api_connections';
}

export interface SafeRetryGuidance {
  code: string;
  retryable_now: boolean;
  retry_action: ExchangePrimaryNextStep;
  dominant_action: ExchangeDominantAction;
}

export interface ExchangeRouteGuidanceEntry {
  route_role: 'proof_owner' | 'management_owner' | 'summary_only' | 'ledger_only';
  handoff_route: '/exchange-sync' | null;
  primary_next_step: ExchangePrimaryNextStep;
  dominant_action: ExchangeDominantAction;
  safe_retry_guidance: SafeRetryGuidance;
}

export interface ApiConnection {
  id: string;
  exchange: SyncExchange;
  permissions: string[];
  last_synced_at: string | null;
  sync_status: SyncStatus;
  error_message: string | null;
  created_at: string;
  last_sync_imported_count?: number | null;
  last_sync_skipped_count?: number | null;
  last_sync_error_count?: number | null;
  last_sync_completed_at?: string | null;
  last_sync_source_label?: string | null;
  health_state?: ExchangeHealthState | null;
  health_reason?: ExchangeHealthReason | null;
  health_blocker?: 'SAFE_EXCHANGE_REPLAY_BLOCKED' | null;
  blocker_classification?: ExchangeBlockerClassification;
  freshness_bucket?: ExchangeFreshnessBucket | null;
  freshness_stale_hours?: number | null;
  freshness_state?: ExchangeFreshnessState | null;
  primary_next_step?: ExchangePrimaryNextStep;
  dominant_action?: ExchangeDominantAction;
  last_successful_sync_proof?: LastSuccessfulSyncProof | null;
  credential_action_required?: boolean;
  provider_auth_action_required?: boolean;
  provider_replay_blocked?: boolean;
  runner_wait_state?: ExchangeRunnerWaitState;
  runner_wait_message?: string | null;
  safe_retry_guidance?: SafeRetryGuidance | null;
  proof_authority?: 'mounted_last_successful_sync' | null;
  provider_replay_scope?: 'management_owned_path' | null;
  route_guidance?: {
    import: ExchangeRouteGuidanceEntry;
    management: ExchangeRouteGuidanceEntry;
    summary: ExchangeRouteGuidanceEntry;
    ledger: ExchangeRouteGuidanceEntry;
  } | null;
}

export interface ConnectExchangeApiRequest {
  exchange: SyncExchange;
  apiKey: string;
  apiSecret: string;
}

export interface ConnectExchangeApiResponse {
  connected: boolean;
  exchange?: SyncExchange;
  permissions?: string[];
  error?: string;
}

export interface ApiSyncResponse {
  imported: number;
  skipped: number;
  errors: number;
  completedAt?: string;
  reconciliation_source?: string;
  reconciliation_exchange?: string;
  reconciliation_token?: string;
  follow_up_dominant_action?: ExchangeDominantAction;
  follow_up_last_sync_source_label?: string | null;
  follow_up_last_successful_sync_proof?: LastSuccessfulSyncProof | null;
}

export interface ApiConnectionsResponse {
  connections: ApiConnection[];
}

export const EXCHANGE_LABELS: Record<SyncExchange, string> = {
  coinbase: 'Coinbase',
  binance: 'Binance',
  kraken: 'Kraken',
};

export const EXCHANGE_DESCRIPTIONS: Record<SyncExchange, string> = {
  coinbase: 'Coinbase Advanced Trade. Generate API keys at coinbase.com → Settings → API.',
  binance: 'Binance Spot. Generate read-only API keys at binance.com → Account → API Management.',
  kraken: 'Kraken. Generate read-only API keys at kraken.com → Security → API.',
};

/**
 * Connect (or re-connect) an exchange via API key + secret.
 * POST /blockchain/import/:userId/api-connect
 */
export async function connectExchangeApi(
  userId: string,
  params: ConnectExchangeApiRequest,
): Promise<ConnectExchangeApiResponse> {
  const res = await apiClient.post<ConnectExchangeApiResponse>(
    `/blockchain/import/${userId}/api-connect`,
    params,
  );
  return res.data;
}

/**
 * Trigger a live transaction sync for a connected exchange.
 * POST /blockchain/import/:userId/api-sync/:exchange
 */
export async function triggerApiSync(
  userId: string,
  exchange: SyncExchange,
): Promise<ApiSyncResponse> {
  const res = await apiClient.post<ApiSyncResponse>(
    `/blockchain/import/${userId}/api-sync/${exchange}`,
  );
  return res.data;
}

/**
 * List all connected exchanges for a user.
 * GET /blockchain/import/:userId/api-connections
 */
export async function getApiConnections(userId: string): Promise<ApiConnectionsResponse> {
  const res = await apiClient.get<ApiConnectionsResponse>(
    `/blockchain/import/${userId}/api-connections`,
  );
  return res.data;
}

/**
 * Disconnect an exchange (delete stored credentials).
 * DELETE /blockchain/import/:userId/api-connections/:exchange
 */
export async function disconnectExchange(
  userId: string,
  exchange: SyncExchange,
): Promise<{ disconnected: boolean }> {
  const res = await apiClient.delete<{ disconnected: boolean }>(
    `/blockchain/import/${userId}/api-connections/${exchange}`,
  );
  return res.data;
}
